import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { deleteCheapestDestination, getCheapestDestinations, getCities } from '../../../../../api/endpoints/cmsContent';
import type { CheapestDestination, City } from '../../../../../api/types/cmsContent';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import {
  ListPageScaffold,
  RowActions,
  useDeleteResource,
  type DataTableColumn,
} from '../../../../../components/common';
import { CheapestDestinationFormDialog } from '../CheapestDestinationFormDialog';

/** `SiteDetailPage` içinde bir sekme; `siteId` sabit prop. */
export default function CheapestDestinationListPage({ siteId }: { siteId: number }) {
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<CheapestDestination | null>(null);

  const query = useQuery({
    queryKey: ['cms', 'content', 'cheapestDestinations', siteId],
    queryFn: () => getCheapestDestinations(siteId),
  });
  const citiesQuery = useQuery({ queryKey: ['cms', 'content', 'cities', siteId], queryFn: () => getCities(siteId) });
  const cities: City[] = useMemo(() => citiesQuery.data?.cities ?? [], [citiesQuery.data]);
  const cityNameById = useMemo(
    () => new Map(cities.map((c) => [c.id, c.name ?? c.displayName ?? `#${c.id}`])),
    [cities]
  );

  const rows: CheapestDestination[] = useMemo(() => query.data?.cheapestDestionations ?? [], [query.data]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr');
    if (!term) return rows;
    return rows.filter((row) =>
      [row.fromCode, row.toCode, row.link, row.airlineCode]
        .filter((v) => v !== undefined && v !== null && v !== '')
        .join(' ')
        .toLocaleLowerCase('tr')
        .includes(term)
    );
  }, [rows, search]);

  const del = useDeleteResource<number>({
    mutationFn: (id) => deleteCheapestDestination(id),
    invalidateKeys: [['cms', 'content', 'cheapestDestinations']],
    successMessage: 'Kayıt silindi.',
  });
  const deletingRow = rows.find((r) => r.id === del.target);

  const columns: DataTableColumn<CheapestDestination>[] = [
    {
      id: 'from',
      header: 'Kalkış',
      cell: (r) => (r.fromCity_Id ? cityNameById.get(r.fromCity_Id) ?? r.fromCode : r.fromCode ?? '—'),
    },
    {
      id: 'to',
      header: 'Varış',
      cell: (r) => (r.toCity_Id ? cityNameById.get(r.toCity_Id) ?? r.toCode : r.toCode ?? '—'),
    },
    { id: 'date', header: 'Tarih', cell: (r) => (r.startDate ? r.startDate.slice(0, 10) : '—') },
    {
      id: 'price',
      header: 'Fiyat',
      cell: (r) => (r.price !== undefined ? `${r.price} ${r.priceCurrency ?? ''}`.trim() : '—'),
    },
    { id: 'airline', header: 'Havayolu', cell: (r) => r.airlineCode ?? '—' },
  ];

  return (
    <ListPageScaffold
      title="En Ucuz Rotalar"
      headerVariant="section"
      createLabel="Yeni Rota"
      onCreate={() => setCreateOpen(true)}
      search={{ value: search, onChange: setSearch, placeholder: 'Kalkış, varış, havayolu, link...' }}
      query={query}
      rows={filteredRows}
      columns={columns}
      getRowKey={(r) => r.id}
      emptyTitle={rows.length === 0 ? 'Kayıtlı rota bulunamadı.' : 'Aramayla eşleşen rota bulunamadı.'}
      rowActions={(r) => <RowActions onEdit={() => setEditing(r)} onDelete={() => del.request(r.id)} />}
    >
      <CheapestDestinationFormDialog open={createOpen} item={null} cities={cities} siteId={siteId} onClose={() => setCreateOpen(false)} />
      <CheapestDestinationFormDialog open={!!editing} item={editing} cities={cities} siteId={siteId} onClose={() => setEditing(null)} />
      <ConfirmDialog
        {...del.confirmDialogProps}
        title="Rotayı sil"
        description={`"${deletingRow?.fromCode ?? ''} → ${deletingRow?.toCode ?? ''}" rotasını silmek istediğinize emin misiniz?`}
      />
    </ListPageScaffold>
  );
}
